import React, { useState, useEffect } from 'react';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { studyProtocolReviewServices } from '../../APIServices/studyProtocolReviewServices';
import { reviewChecklistMetaServices } from '../../APIServices/reviewChecklistMetaServices';

function CSRListingReviewModule() {
  const { state, audit, currentUser } = useData();
  const toast = useToast();
  
  const [activeTab, setActiveTab] = useState('upload');
  const [projectId, setProjectId] = useState(null);
  const [listings, setListings] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [findings, setFindings] = useState([]);
  const [score, setScore] = useState({ total: 0, pass: 0, fail: 0, needs: 0, critical: 0, major: 0, readiness: 'Pending' });
  const [reviewRecord, setReviewRecord] = useState(null);
  const [listingChecklistMeta, setListingChecklistMeta] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [checklistFilter, setChecklistFilter] = useState({
    severity: 'all',
    status: 'all',
    listingId: 'all',
    search: ''
  });

  const tabs = [
    { id: 'upload', label: 'Upload', icon: 'ti-upload' },
    { id: 'checklist', label: 'Checklist', icon: 'ti-checklist' },
    { id: 'report', label: 'Report', icon: 'ti-file-text' }
  ];

  useEffect(() => {
    setClients(state.clients || []);
    setProjects(state.projects || []);
  }, [state]);

  useEffect(() => {
    const loadChecklistMeta = async () => {
      const data = await reviewChecklistMetaServices.getReviewChecklistData('active', 'csr_listing_review', '');
      setListingChecklistMeta(data?.data || []);
    };

    loadChecklistMeta();
  }, []);

  useEffect(() => {
    const loadSavedReview = async () => {
      if (!projectId) {
        setReviewRecord(null);
        setListings([]);
        setFindings([]);
        setSelectedListing(null);
        setScore({ total: 0, pass: 0, fail: 0, needs: 0, critical: 0, major: 0, readiness: 'Pending' });
        return;
      }

      const data = await studyProtocolReviewServices.getStudyProtocolReview('csr_listing_review', 'active', projectId);
      const row = data?.data?.[0];

      if (!row) {
        setReviewRecord(null);
        setListings([]);
        setFindings([]);
        setSelectedListing(null);
        setScore({ total: 0, pass: 0, fail: 0, needs: 0, critical: 0, major: 0, readiness: 'Pending' });
        return;
      }

      setReviewRecord(row);
      setListings(row.listings || []);
      setFindings(row.findings || []);
      setScore(row.score || { total: 0, pass: 0, fail: 0, needs: 0, critical: 0, major: 0, readiness: 'Pending' });
    };

    loadSavedReview();
  }, [projectId]);

  const saveListingReview = async (nextListings = listings, nextFindings = findings, nextScore = score) => {
    if (!projectId) {
      toast('Please select a project first', 'warn');
      return;
    }

    const payload = {
      id: reviewRecord?._id,
      reviewId: reviewRecord?.reviewId,
      reviewType: 'csr_listing_review',
      projectId,
      listings: nextListings,
      findings: nextFindings,
      score: nextScore,
      fileName: nextListings?.[0]?.fileName || reviewRecord?.fileName || '',
      status: 'in_review',
    };

    const data = reviewRecord?._id
      ? await studyProtocolReviewServices.updateStudyProtocolReview(payload)
      : await studyProtocolReviewServices.addNewStudyProtocolReview(payload);

    if (data?.data) {
      setReviewRecord(data.data);
    }

    toast('CSR listing review saved successfully', 'ok');
  };

  const saveChecklistReview = async () => {
    const nextScore = calculateScore(findings);
    setScore(nextScore);
    await saveListingReview(listings, findings, nextScore);
    audit('CSR_LISTING_CHECKLIST_SAVE', projectId || 'no-project', 'CSR listing checklist saved', { projectId });
  };

  const saveReport = async () => {
    const nextScore = calculateScore(findings);
    setScore(nextScore);
    await saveListingReview(listings, findings, nextScore);
    audit('CSR_LISTING_REPORT_SAVE', projectId || 'no-project', 'CSR listing report saved', { projectId });
  };

  const handleListingUpload = async (files) => {
    const uploaded = [];
    for (const file of files) {
      try {
        const text = await file.text();
        const metadata = extractListingMetadata(text);
        const classification = classifyListing(text, file.name);
        const listing = {
          id: 'list-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
          projectId,
          fileName: file.name,
          fileType: file.name.split('.').pop(),
          extractedText: text,
          metadata,
          classifiedType: classification.type,
          classificationConfidence: classification.confidence,
          uploadedAt: new Date().toISOString()
        };
        uploaded.push(listing);
        toast(`${file.name} uploaded`, 'ok');
        audit('CSR_LISTING_UPLOAD', file.name, 'Listing uploaded', { projectId });
      } catch (error) {
        toast('Error uploading ' + file.name + ': ' + error.message, 'error');
      }
    }
    const nextListings = [...listings, ...uploaded];
    setListings(nextListings);
    const { nextFindings, nextScore } = runAutoChecks(uploaded);
    await saveListingReview(nextListings, nextFindings, nextScore);
  };

  const extractListingMetadata = (text) => {
    const meta = {};
    let match = text.match(/(?:study\s*(?:number|no\.?)\s*[:\-]?\s*)([A-Z]{2,}[\-/_][A-Z0-9\-/_]{3,30})/i);
    if (match) meta.studyNumber = match[1];
    match = text.match(/appendix\s+(\d{1,2}(?:\.\d{1,2}){1,3})/i);
    if (match) meta.appendixNumber = match[1];
    const pages = text.match(/---\s*Page\s+\d+\s*---/gi);
    if (pages) meta.pageCount = pages.length;
    const subjects = [];
    const subjMatch = text.matchAll(/subject(?:\s+no\.?|\s+number|\s+id)?\s*[:\-]?\s*(\d{1,3})/gi);
    for (const m of subjMatch) {
      const n = parseInt(m[1], 10);
      if (n >= 1 && n <= 999) subjects.push(n);
    }
    meta.subjects = [...new Set(subjects)].sort((a, b) => a - b);
    return meta;
  };

  const classifyListing = (text, fileName) => {
    const combined = text.toLowerCase() + ' ' + fileName.toLowerCase();
    const scores = { exclusion: 0, concentration: 0, pk: 0, safety: 0, deviation: 0 };

    if (/subject(s)?\s+excluded/.test(combined)) scores.exclusion += 3;
    if (/excluded\s+from\s+(the\s+)?(efficacy|pk|statistical)/.test(combined)) scores.exclusion += 4;
    if (/(plasma|serum|blood)\s+concentration/.test(combined)) scores.concentration += 4;
    if (/concentration\s+(data|values?|listing)/.test(combined)) scores.concentration += 3;
    if (/individual\s+(pharmacokinetic|pk)/.test(combined)) scores.pk += 4;
    if (/\bcmax\b/i.test(text)) scores.pk += 2;
    if (/\bauc[ti0\-_]/i.test(text)) scores.pk += 2;
    if (/adverse\s+event(s)?/.test(combined)) scores.safety += 4;
    if (/protocol\s+deviation/.test(combined)) scores.deviation += 4;

    let best = 'unknown', bestScore = 0;
    for (const k of Object.keys(scores)) {
      if (scores[k] > bestScore) { bestScore = scores[k]; best = k; }
    }
    return { type: bestScore < 2 ? 'unknown' : best, confidence: bestScore };
  };

  const getChecksForType = (type) => {
    if (listingChecklistMeta.length > 0) {
      return listingChecklistMeta.map((item, index) => ({
        id: item.autoRuleKey || item.reviewChecklistId || item._id || `CHK-${index + 1}`,
        point: item.checkpoint || '',
        sev: item.severity || 'Major',
        cat: item.category || 'General',
      }));
    }

    const allChecks = {
      exclusion: [
        { id: 'EXC-001', point: 'Excluded subject number is clearly stated', sev: 'Critical' },
        { id: 'EXC-002', point: 'Reason for exclusion is provided', sev: 'Critical' },
        { id: 'EXC-003', point: 'Timing of exclusion/withdrawal is provided', sev: 'Major' },
        { id: 'EXC-004', point: 'Impact on PK/statistical analysis is explained', sev: 'Critical' }
      ],
      concentration: [
        { id: 'CONC-001', point: 'Treatment is present', sev: 'Critical' },
        { id: 'CONC-002', point: 'Subject number is present', sev: 'Critical' },
        { id: 'CONC-003', point: 'Period is present', sev: 'Major' },
        { id: 'CONC-004', point: 'Time points are complete and in correct order', sev: 'Critical' }
      ],
      pk: [
        { id: 'PK-001', point: 'PK parameters are listed subject-wise', sev: 'Critical' },
        { id: 'PK-002', point: 'Treatment, subject, period, and sequence are present', sev: 'Critical' },
        { id: 'PK-003', point: 'Cmax is present with correct unit', sev: 'Critical' }
      ],
      unknown: [
        { id: 'GEN-001', point: 'Study title is present and consistent', sev: 'Major' },
        { id: 'GEN-002', point: 'Study number is present and consistent', sev: 'Critical' }
      ]
    };
    return allChecks[type] || allChecks.unknown;
  };

  const runCheck = (listing, check) => {
    const text = listing.extractedText || '';
    const rules = {
      'EXC-001': () => listing.metadata?.subjects?.length > 0 ? { status: 'Pass', evidence: 'Subjects found' } : { status: 'Fail', evidence: 'No subjects found' },
      'EXC-002': () => /reason\s+for\s+(exclusion|withdrawal)|due to|because of/i.test(text) ? { status: 'Pass', evidence: 'Reason found' } : { status: 'Fail', evidence: 'No reason found' },
      'EXC-003': () => /period\s+[1IVX]|period\s+\d|at\s+\d+\.?\d*\s*h/i.test(text) ? { status: 'Pass', evidence: 'Timing found' } : { status: 'Needs Review', evidence: 'No timing found' },
      'EXC-004': () => /not\s+(considered|included).{0,60}(pk|statistical|analysis)/i.test(text) ? { status: 'Pass', evidence: 'Impact stated' } : { status: 'Needs Review', evidence: 'No impact statement' },
      'CONC-001': () => /\btreatment\b/i.test(text) ? { status: 'Pass', evidence: 'Treatment found' } : { status: 'Fail', evidence: 'Treatment missing' },
      'CONC-002': () => /\bsubject\b/i.test(text) ? { status: 'Pass', evidence: 'Subject found' } : { status: 'Fail', evidence: 'Subject missing' },
      'CONC-003': () => /\bperiod\b/i.test(text) ? { status: 'Pass', evidence: 'Period found' } : { status: 'Fail', evidence: 'Period missing' },
      'CONC-004': () => listing.metadata?.timePoints?.length > 3 ? { status: 'Pass', evidence: 'Time points: ' + listing.metadata.timePoints.length } : { status: 'Needs Review', evidence: 'Few time points' },
      'PK-001': () => /\bcmax\b/i.test(text) || /\bauc[ti0\-_]/i.test(text) ? { status: 'Pass', evidence: 'PK parameters found' } : { status: 'Fail', evidence: 'No PK parameters' },
      'PK-002': () => /\b(treatment|subject|period|sequence)\b/i.test(text) ? { status: 'Pass', evidence: 'Identifiers found' } : { status: 'Fail', evidence: 'Missing identifiers' },
      'PK-003': () => /\bcmax\b/i.test(text) ? { status: 'Pass', evidence: 'Cmax found' } : { status: 'Fail', evidence: 'Cmax missing' },
      'GEN-001': () => listing.metadata?.studyNumber ? { status: 'Pass', evidence: listing.metadata.studyNumber } : { status: 'Needs Review', evidence: 'No study number' },
      'GEN-002': () => listing.metadata?.studyNumber ? { status: 'Pass', evidence: listing.metadata.studyNumber } : { status: 'Fail', evidence: 'No study number' }
    };
    return rules[check.id] ? rules[check.id]() : { status: 'Needs Review', evidence: 'Auto-check not implemented' };
  };

  const runAutoChecks = (listings) => {
    const allFindings = [];
    listings.forEach(listing => {
      const checks = getChecksForType(listing.classifiedType);
      checks.forEach(check => {
        const result = runCheck(listing, check);
        if (result) {
          allFindings.push({
            id: 'find-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
            listingId: listing.id,
            ...check,
            ...result,
            autoSuggested: true,
            createdAt: new Date().toISOString()
          });
        }
      });
    });
    const nextFindings = [...findings, ...allFindings];
    const nextScore = calculateScore(nextFindings);
    setFindings(nextFindings);
    setScore(nextScore);
    return { nextFindings, nextScore };
  };

  const calculateScore = (allFindings) => {
    const f = allFindings || [];
    const total = f.length;
    const pass = f.filter(x => x.status === 'Pass').length;
    const fail = f.filter(x => x.status === 'Fail').length;
    const needs = f.filter(x => x.status === 'Needs Review').length;
    const critical = f.filter(x => x.status === 'Fail' && x.sev === 'Critical').length;
    const major = f.filter(x => x.status === 'Fail' && x.sev === 'Major').length;
    
    let readiness = 'Pending';
    if (critical > 0) readiness = 'Not Ready';
    else if (major > 0 || needs > 0) readiness = 'Pending Review';
    else if (pass > 0 && fail === 0) readiness = 'Ready';
    else if (fail === 0 && needs === 0) readiness = 'Ready';

    return { total, pass, fail, needs, critical, major, readiness };
  };

  const clearChecklistFilters = () => {
    setChecklistFilter({ severity: 'all', status: 'all', listingId: 'all', search: '' });
  };

  const getFilteredFindings = () => {
    const search = checklistFilter.search.trim().toLowerCase();
    return findings.filter((f) => {
      const severityOk = checklistFilter.severity === 'all' || f.sev === checklistFilter.severity;
      const statusOk = checklistFilter.status === 'all' || f.status === checklistFilter.status;
      const listingOk = checklistFilter.listingId === 'all' || f.listingId === checklistFilter.listingId;
      const searchOk =
        !search ||
        String(f.id || '').toLowerCase().includes(search) ||
        String(f.point || '').toLowerCase().includes(search) ||
        String(f.evidence || '').toLowerCase().includes(search) ||
        String(f.cat || '').toLowerCase().includes(search);
      return severityOk && statusOk && listingOk && searchOk;
    });
  };

  const escapeHtml = (value) => {
    const str = String(value || '');
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const buildReportHtml = () => {
    const selectedProject = projects.find((p) => p.id === projectId);
    const selectedClient = clients.find((c) => c.id === selectedProject?.clientId);
    const criticalMajorFindings = findings.filter((f) => f.status === 'Fail' && (f.sev === 'Critical' || f.sev === 'Major'));
    const recommendations = findings.filter((f) => f.status === 'Fail').slice(0, 5);
    const generatedAt = new Date().toLocaleString();

    const listingRows = listings.map((l, i) => {
      const lf = findings.filter((f) => f.listingId === l.id);
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${escapeHtml(l.fileName)}</td>
          <td>${escapeHtml(l.classifiedType)}</td>
          <td>${escapeHtml(l.metadata?.appendixNumber || '-')}</td>
          <td>${escapeHtml(l.metadata?.subjects?.length || 0)}</td>
          <td>${escapeHtml(lf.length)}</td>
        </tr>
      `;
    }).join('');

    const findingsRows = criticalMajorFindings.length > 0
      ? criticalMajorFindings.map((f, i) => `
        <tr>
          <td>${i + 1}</td>
          <td>${escapeHtml(f.id)}</td>
          <td>${escapeHtml(f.point)}</td>
          <td>${escapeHtml(f.sev)}</td>
          <td>${escapeHtml(f.evidence || '-')}</td>
          <td>${escapeHtml(f.status)}</td>
        </tr>
      `).join('')
      : '<tr><td colspan="6" style="text-align:center; padding:10px;">No Critical or Major findings</td></tr>';

    const recommendationRows = recommendations.length > 0
      ? recommendations.map((f) => `<li><strong>${escapeHtml(f.id)}:</strong> ${escapeHtml(f.point)} - ${escapeHtml(f.evidence || 'Please verify')}</li>`).join('')
      : '<li>No recommendations - all checks passed</li>';

    return `
      <div>
        <h1 style="margin-bottom: 8px;">CSR Listing Review Report</h1>
        <div style="margin-bottom: 12px;"><strong>Project:</strong> ${escapeHtml(selectedProject?.name || 'Untitled Project')}</div>
        <div style="margin-bottom: 4px;"><strong>Protocol Number:</strong> ${escapeHtml(selectedProject?.protocolNumber || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Client:</strong> ${escapeHtml(selectedClient?.name || '-')}</div>
        <div style="margin-bottom: 4px;"><strong>Reviewer:</strong> ${escapeHtml(currentUser?.name || '-')}</div>
        <div style="margin-bottom: 16px;"><strong>Generated:</strong> ${escapeHtml(generatedAt)}</div>

        <h2>1. Review Summary</h2>
        <p><strong>Readiness:</strong> ${escapeHtml(score.readiness)}</p>
        <p><strong>Total Listings:</strong> ${escapeHtml(listings.length)} | <strong>Total Checks:</strong> ${escapeHtml(score.total)}</p>
        <p><strong>Pass:</strong> ${escapeHtml(score.pass)} | <strong>Fail:</strong> ${escapeHtml(score.fail)} | <strong>Needs Review:</strong> ${escapeHtml(score.needs)}</p>
        <p><strong>Critical Fails:</strong> ${escapeHtml(score.critical)} | <strong>Major Fails:</strong> ${escapeHtml(score.major)}</p>

        <h2>2. Uploaded Listings</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th>#</th>
              <th>File</th>
              <th>Classification</th>
              <th>Appendix</th>
              <th>Subjects</th>
              <th>Checks</th>
            </tr>
          </thead>
          <tbody>
            ${listingRows || '<tr><td colspan="6" style="text-align:center; padding:10px;">No listings uploaded</td></tr>'}
          </tbody>
        </table>

        <h2>3. Critical & Major Findings</h2>
        <table border="1" cellspacing="0" cellpadding="6" style="border-collapse: collapse; width: 100%; font-size: 12px;">
          <thead>
            <tr>
              <th>#</th>
              <th>Check ID</th>
              <th>Review Point</th>
              <th>Severity</th>
              <th>Evidence</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${findingsRows}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol>
          ${recommendationRows}
        </ol>
      </div>
    `;
  };

  const exportReportPdf = () => {
    const reportHtml = buildReportHtml();
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      toast('Popup blocked. Please allow popups to export PDF.', 'warn');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>CSR Listing Review Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #111; line-height: 1.45; }
            h1, h2, h3 { margin: 0 0 8px; }
            h2 { margin-top: 20px; }
            p { margin: 0 0 8px; }
            table { margin-top: 8px; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${reportHtml}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    audit('CSR_LISTING_REPORT_EXPORT_PDF', reviewRecord?.fileName || 'CSR listing report', 'CSR listing report exported as PDF', { projectId });
    toast('Report opened for PDF export', 'ok');
  };

  const exportReportDoc = () => {
    const reportHtml = buildReportHtml();
    const docContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="utf-8" />
          <title>CSR Listing Review Report</title>
        </head>
        <body>${reportHtml}</body>
      </html>
    `;

    const blob = new Blob(['\ufeff', docContent], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const selectedProject = projects.find((p) => p.id === projectId);
    const safeName = (selectedProject?.name || 'csr-listing-review-report').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    audit('CSR_LISTING_REPORT_EXPORT_DOC', reviewRecord?.fileName || 'CSR listing report', 'CSR listing report exported as DOC', { projectId });
    toast('DOC export completed', 'ok');
  };

  const computeScore = (allFindings) => {
    setScore(calculateScore(allFindings || findings));
  };

  useEffect(() => {
    computeScore(findings);
  }, [findings]);

  const renderUploadTab = () => (
    <div>
      <div className="card card-pad mb-4">
        <div className="card-title">
          <i className="ti ti-cloud-upload" /> Upload CSR Listings
        </div>
        <div className="field">
          <label className="field-label">Select Project</label>
          <select 
            className="inp" 
            value={projectId || ''} 
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">— Select a project —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name || p.code}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Upload Listing Files</label>
          <input 
            type="file" 
            className="inp" 
            accept=".pdf,.docx,.txt,.html" 
            multiple
            onChange={(e) => {
              if (e.target.files.length > 0) handleListingUpload(e.target.files);
            }}
          />
          <div className="field-hint">Supported: PDF, DOCX, TXT, HTML (multiple files allowed)</div>
        </div>
        {listings.length > 0 && (
          <div className="alert info" style={{ marginTop: 8, padding: 10, borderRadius: 6, background: 'var(--accent-soft)', border: '1px solid var(--accent-3)' }}>
            <i className="ti ti-files" /> {listings.length} listing(s) uploaded
          </div>
        )}
        <div className="mt-3">
          <button className="btn btn-primary" onClick={() => saveListingReview()} disabled={!projectId}>
            <i className="ti ti-save" /> Save Listing Review
          </button>
        </div>
      </div>

      <div className="card card-pad">
        <div className="card-title">
          <i className="ti ti-files" /> Uploaded Listings
        </div>
        {listings.length === 0 ? (
          <div className="empty">
            <i className="ti ti-file-off" style={{ fontSize: 28 }} />
            <div className="empty-title">No listings uploaded</div>
            <div className="empty-desc">Upload CSR appendix listings to begin review</div>
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Classification</th>
                  <th>Appendix</th>
                  <th>Subjects</th>
                  <th>Pages</th>
                  <th>Checks</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {listings.map(l => {
                  const listingFindings = findings.filter(f => f.listingId === l.id);
                  const pass = listingFindings.filter(f => f.status === 'Pass').length;
                  const fail = listingFindings.filter(f => f.status === 'Fail').length;
                  return (
                    <tr key={l.id}>
                      <td>{l.fileName}</td>
                      <td>
                        <span className={`tag tag-${l.classifiedType === 'unknown' ? 'default' : 'info'}`}>
                          {l.classifiedType}
                        </span>
                      </td>
                      <td>{l.metadata?.appendixNumber || '—'}</td>
                      <td>{l.metadata?.subjects?.length || 0}</td>
                      <td>{l.metadata?.pageCount || '—'}</td>
                      <td>
                        <span className="tag tag-ok">{pass} pass</span>
                        <span className="tag tag-danger">{fail} fail</span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-ghost" onClick={() => setSelectedListing(l)}>
                          <i className="ti ti-eye" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedListing && (
        <div className="card card-pad mt-4">
          <div className="flex between mb-2">
            <div className="card-title">
              <i className="ti ti-file-info" /> Listing Preview: {selectedListing.fileName}
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => setSelectedListing(null)}>
              <i className="ti ti-x" /> Close
            </button>
          </div>
          <div className="tiny muted mb-2">
            Type: {selectedListing.classifiedType} | Appendix: {selectedListing.metadata?.appendixNumber || '—'} | Subjects: {selectedListing.metadata?.subjects?.length || 0}
          </div>
          <textarea
            className="ta"
            value={selectedListing.extractedText || ''}
            readOnly
            style={{ minHeight: 180, width: '100%', padding: 16 }}
          />
        </div>
      )}
    </div>
  );

  const renderChecklistTab = () => {
    const filteredFindings = getFilteredFindings();

    return (
    <div>
      <div className="flex between mb-3">
        <div className="card-title">
          <i className="ti ti-checklist" /> CSR Listing Checklist
          <span className="tiny muted" style={{ marginLeft: 8 }}>{filteredFindings.length} of {findings.length} checks</span>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={() => setShowFilters((v) => !v)}>
            <i className="ti ti-filter" /> Filter
          </button>
          <button className="btn btn-sm btn-primary" onClick={async () => {
            const updatedFindings = findings.map((f) => ({
              ...f,
              status: 'Pass',
              evidence: f.evidence || 'Validated manually'
            }));
            const nextScore = calculateScore(updatedFindings);
            setFindings(updatedFindings);
            setScore(nextScore);
            await saveListingReview(listings, updatedFindings, nextScore);
            toast('All checks validated', 'ok');
          }}>
            <i className="ti ti-check" /> Validate All
          </button>
          <button className="btn btn-sm btn-primary" onClick={saveChecklistReview}>
            <i className="ti ti-device-floppy" /> Save Checklist
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="card card-pad mb-3">
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="field">
              <label className="field-label">Search</label>
              <input
                className="inp"
                value={checklistFilter.search}
                onChange={(e) => setChecklistFilter((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Check id, category, point, evidence"
              />
            </div>
            <div className="field">
              <label className="field-label">Severity</label>
              <select
                className="inp"
                value={checklistFilter.severity}
                onChange={(e) => setChecklistFilter((prev) => ({ ...prev, severity: e.target.value }))}
              >
                <option value="all">All</option>
                <option value="Critical">Critical</option>
                <option value="Major">Major</option>
                <option value="Minor">Minor</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">Status</label>
              <select
                className="inp"
                value={checklistFilter.status}
                onChange={(e) => setChecklistFilter((prev) => ({ ...prev, status: e.target.value }))}
              >
                <option value="all">All</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Needs Review">Needs Review</option>
              </select>
            </div>
            <div className="field">
              <label className="field-label">Listing</label>
              <select
                className="inp"
                value={checklistFilter.listingId}
                onChange={(e) => setChecklistFilter((prev) => ({ ...prev, listingId: e.target.value }))}
              >
                <option value="all">All</option>
                {listings.map((l) => (
                  <option key={l.id} value={l.id}>{l.fileName}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2" style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-ghost" onClick={clearChecklistFilters}>
              <i className="ti ti-eraser" /> Clear
            </button>
          </div>
        </div>
      )}

      <div className="score-banner" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12,
        background: 'var(--surface-2)',
        padding: 16,
        borderRadius: 8,
        marginBottom: 16
      }}>
        <div>
          <div className="tiny muted">Total Checks</div>
          <div className="metric-value" style={{ fontSize: 24 }}>{score.total}</div>
        </div>
        <div>
          <div className="tiny muted">Pass</div>
          <div className="metric-value" style={{ fontSize: 24, color: 'var(--ok)' }}>{score.pass}</div>
        </div>
        <div>
          <div className="tiny muted">Fail</div>
          <div className="metric-value" style={{ fontSize: 24, color: 'var(--danger)' }}>{score.fail}</div>
        </div>
        <div>
          <div className="tiny muted">Needs Review</div>
          <div className="metric-value" style={{ fontSize: 24, color: 'var(--warn)' }}>{score.needs}</div>
        </div>
        <div>
          <div className="tiny muted">Critical Fails</div>
          <div className="metric-value" style={{ fontSize: 24, color: 'var(--danger)' }}>{score.critical}</div>
        </div>
        <div>
          <div className="tiny muted">Readiness</div>
          <div className="metric-value" style={{ fontSize: 16 }}>{score.readiness}</div>
        </div>
      </div>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 80 }}>Check ID</th>
              <th>Category</th>
              <th>Review Point</th>
              <th style={{ width: 80 }}>Severity</th>
              <th style={{ width: 80 }}>Status</th>
              <th style={{ width: 120 }}>Evidence</th>
            </tr>
          </thead>
          <tbody>
            {filteredFindings.map(f => (
              <tr key={f.id} className={f.status === 'Fail' && f.sev === 'Critical' ? 'row-crit' : f.status === 'Fail' ? 'row-major' : ''}>
                <td className="mono">{f.id}</td>
                <td>{f.cat || 'General'}</td>
                <td>{f.point}</td>
                <td><span className={`badge ${f.sev === 'Critical' ? 'crit' : f.sev === 'Major' ? 'major' : 'minor'}`}>{f.sev}</span></td>
                <td>
                  <span className={`badge ${f.status === 'Pass' ? 'ok' : f.status === 'Fail' ? 'crit' : 'major'}`}>
                    {f.status}
                  </span>
                </td>
                <td className="tiny">{f.evidence || '—'}</td>
              </tr>
            ))}
            {filteredFindings.length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  No checks found for selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  };

  // ==================== CSR-STYLE REPORT ====================
  const renderReportTab = () => (
    <div>
      <div className="flex between mb-3">
        <div className="card-title">
          <i className="ti ti-file-text" /> CSR Listing Review Report
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost" onClick={exportReportPdf}>
            <i className="ti ti-download" /> Export PDF
          </button>
          <button className="btn btn-sm btn-ghost" onClick={exportReportDoc}>
            <i className="ti ti-file-type-doc" /> Export DOC
          </button>
          <button className="btn btn-sm btn-primary" onClick={saveReport}>
            <i className="ti ti-save" /> Save Report
          </button>
        </div>
      </div>

      <div className="report-paper draft card-pad">
        <div className="cover">
          <div className="deck">CSR Listing Review Report</div>
          <h1>{projects.find(p => p.id === projectId)?.name || 'Untitled Project'}</h1>
          <div className="muted" style={{ marginTop: 8 }}>Protocol Number: {projects.find(p => p.id === projectId)?.protocolNumber || '—'}</div>
          <div className="meta-grid" style={{ marginTop: 24 }}>
            <div><div className="k">Client</div><div className="v">{clients.find(c => c.id === projects.find(p => p.id === projectId)?.clientId)?.name || '—'}</div></div>
            <div><div className="k">Listings Reviewed</div><div className="v">{listings.length}</div></div>
            <div><div className="k">Reviewer</div><div className="v">{currentUser?.name || '—'}</div></div>
            <div><div className="k">Report Generated</div><div className="v">{new Date().toLocaleString()}</div></div>
            <div><div className="k">Readiness</div><div className="v">{score.readiness}</div></div>
          </div>
        </div>

        <h2>1. Review Summary</h2>
        <div className={`verdict ${score.critical > 0 ? 'crit' : score.major > 0 ? 'major' : score.readiness === 'Ready' ? 'ok' : ''}`}>
          <div className="label">Overall CSR Listing Readiness</div>
          <div className="text">{score.readiness}</div>
        </div>
        <div className="meta-grid">
          <div><div className="k">Total Listings</div><div className="v">{listings.length}</div></div>
          <div><div className="k">Total Checks</div><div className="v">{score.total}</div></div>
          <div><div className="k">Pass</div><div className="v" style={{ color: 'var(--ok)' }}>{score.pass}</div></div>
          <div><div className="k">Fail</div><div className="v" style={{ color: 'var(--danger)' }}>{score.fail}</div></div>
          <div><div className="k">Needs Review</div><div className="v" style={{ color: 'var(--warn)' }}>{score.needs}</div></div>
          <div><div className="k">Critical Fails</div><div className="v" style={{ color: 'var(--danger)' }}>{score.critical}</div></div>
          <div><div className="k">Major Fails</div><div className="v" style={{ color: 'var(--warn)' }}>{score.major}</div></div>
        </div>

        <h2>2. Uploaded Listings</h2>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>File</th>
              <th>Classification</th>
              <th>Appendix</th>
              <th>Subjects</th>
              <th>Checks</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((l, i) => {
              const lf = findings.filter(f => f.listingId === l.id);
              return (
                <tr key={l.id}>
                  <td>{i + 1}</td>
                  <td><strong>{l.fileName}</strong></td>
                  <td>{l.classifiedType}</td>
                  <td>{l.metadata?.appendixNumber || '—'}</td>
                  <td>{l.metadata?.subjects?.length || 0}</td>
                  <td>{lf.length}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <h2>3. Critical &amp; Major Findings</h2>
        <table className="tbl-findings">
          <thead>
            <tr>
              <th>#</th>
              <th>Check ID</th>
              <th>Review Point</th>
              <th>Severity</th>
              <th>Evidence</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {findings.filter(f => f.status === 'Fail' && (f.sev === 'Critical' || f.sev === 'Major')).map((f, i) => (
              <tr key={f.id}>
                <td>{i + 1}</td>
                <td><strong>{f.id}</strong></td>
                <td>{f.point}</td>
                <td><span className={`badge ${f.sev === 'Critical' ? 'crit' : 'major'}`}>{f.sev}</span></td>
                <td className="tiny">{f.evidence || '—'}</td>
                <td><span className="badge crit">{f.status}</span></td>
              </tr>
            ))}
            {findings.filter(f => f.status === 'Fail' && (f.sev === 'Critical' || f.sev === 'Major')).length === 0 && (
              <tr>
                <td colSpan={6} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  No Critical or Major findings
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <h2>4. Recommendations</h2>
        <ol style={{ marginLeft: 22, fontSize: 13 }}>
          {findings.filter(f => f.status === 'Fail').slice(0, 5).map((f, i) => (
            <li key={f.id}><strong>{f.id}:</strong> {f.point} — {f.evidence || 'Please verify'}</li>
          ))}
          {findings.filter(f => f.status === 'Fail').length === 0 && (
            <li>No recommendations — all checks passed</li>
          )}
          {findings.filter(f => f.status === 'Fail').length > 5 && (
            <li>... and {findings.filter(f => f.status === 'Fail').length - 5} more issues</li>
          )}
        </ol>

        <div className="sig-block">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 24 }}>
            <div>
              <div className="k">Reviewer</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>{currentUser?.name || '—'}<br /><span className="tiny muted">Date: {new Date().toLocaleDateString()}</span></div>
            </div>
            <div>
              <div className="k">Approved by</div>
              <div style={{ height: 48, borderBottom: '1px solid var(--ink)', margin: '8px 0' }}></div>
              <div>—<br /><span className="tiny muted">Signature &amp; Date</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="csr-listing-review-module">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Study Document Review</div>
          <h1 className="page-title">CSR Listing Review</h1>
          <p className="page-sub">Upload CSR appendix listings, run automated checks, and generate reports.</p>
        </div>
        <div className="flex gap-2">
          <span className="tag tag-ok tag-dot">Validated</span>
          <span className="tag tag-info">ALCOA+</span>
        </div>
      </div>

      <div className="tabs" style={{ 
        display: 'flex', 
        gap: 4, 
        borderBottom: '1px solid var(--line)',
        marginBottom: 24,
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              color: activeTab === tab.id ? 'var(--ink)' : 'var(--ink-3)',
              cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-3)' : '2px solid transparent',
              marginBottom: -1,
              whiteSpace: 'nowrap'
            }}
          >
            <i className={`ti ${tab.icon}`} style={{ marginRight: 6 }} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {activeTab === 'upload' && renderUploadTab()}
        {activeTab === 'checklist' && renderChecklistTab()}
        {activeTab === 'report' && renderReportTab()}
      </div>

      <style>{`
        .csr-listing-review-module .row-crit td {
          background: var(--danger-soft);
        }
        .csr-listing-review-module .row-major td {
          background: var(--warn-soft);
        }
        .csr-listing-review-module .badge {
          display: inline-block;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 600;
          border-radius: 2px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border: 1px solid currentColor;
        }
        .csr-listing-review-module .badge.crit {
          color: var(--danger);
          background: var(--danger-soft);
        }
        .csr-listing-review-module .badge.major {
          color: var(--warn);
          background: var(--warn-soft);
        }
        .csr-listing-review-module .badge.minor {
          color: var(--info);
          background: var(--info-soft);
        }
        .csr-listing-review-module .badge.ok {
          color: var(--ok);
          background: var(--ok-soft);
        }
        .csr-listing-review-module .badge.draft {
          color: var(--ink-3);
          background: var(--surface-2);
        }
        .csr-listing-review-module .badge.info {
          color: var(--teal);
          background: var(--teal-soft);
        }
        .csr-listing-review-module .alert.info {
          background: var(--info-soft);
          border: 1px solid var(--info);
          color: var(--info);
          padding: 12px;
          border-radius: 6px;
        }
        .csr-listing-review-module .report-paper {
          background: #ffffff;
          border: 1px solid var(--line);
          padding: 32px 40px;
          font-size: 13px;
          line-height: 1.65;
          color: var(--ink);
          position: relative;
          border-radius: 8px;
        }
        .csr-listing-review-module .report-paper.draft::before {
          content: 'DRAFT';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-32deg);
          font-size: 100px;
          font-weight: 700;
          color: rgba(0,0,0,0.03);
          pointer-events: none;
          letter-spacing: -0.04em;
        }
        .csr-listing-review-module .report-paper .cover {
          text-align: center;
          padding: 0 0 24px;
          border-bottom: 2px solid var(--ink);
          margin-bottom: 24px;
        }
        .csr-listing-review-module .report-paper .deck {
          font-style: italic;
          color: var(--accent-3);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          font-size: 12px;
          margin-bottom: 12px;
        }
        .csr-listing-review-module .report-paper .verdict {
          padding: 14px;
          border: 2px solid var(--ink);
          background: var(--surface-2);
          margin: 14px 0;
          text-align: center;
        }
        .csr-listing-review-module .report-paper .verdict.crit {
          border-color: var(--danger);
          background: var(--danger-soft);
        }
        .csr-listing-review-module .report-paper .verdict.major {
          border-color: var(--warn);
          background: var(--warn-soft);
        }
        .csr-listing-review-module .report-paper .verdict.ok {
          border-color: var(--ok);
          background: var(--ok-soft);
        }
        .csr-listing-review-module .report-paper .verdict .label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: var(--ink-3);
        }
        .csr-listing-review-module .report-paper .verdict .text {
          font-size: 18px;
          font-weight: 600;
          margin-top: 4px;
        }
        .csr-listing-review-module .report-paper .meta-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px 24px;
          margin: 18px 0;
          font-size: 13px;
        }
        .csr-listing-review-module .report-paper .meta-grid .k {
          color: var(--ink-3);
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .csr-listing-review-module .report-paper .meta-grid .v {
          font-weight: 500;
        }
        .csr-listing-review-module .report-paper .sig-block {
          margin-top: 36px;
          padding-top: 14px;
          border-top: 1px solid var(--line);
          font-size: 12px;
        }
        .csr-listing-review-module .report-paper .sig-block .k {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--ink-3);
        }
        .csr-listing-review-module .score-banner {
          background: var(--surface-2);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }
        .csr-listing-review-module .score-banner .metric-value {
          font-family: var(--display);
          font-size: 24px;
          font-weight: 500;
          line-height: 1.1;
        }
      `}</style>
    </div>
  );
}

export default CSRListingReviewModule;